/**
 * DI-3-5 — the analyst's flight facts, pure (story §2.3).
 *
 * `DataAcquisition` (or the detail) → the chip models the list's "Flights"
 * column and the Analysis header's Flights section render. Every number is
 * the WORKER's value as lyse/v1 8.6.0 serves it; this module formats and
 * sums (the enabled flights' images), it never computes an offset or a count.
 *
 * Fixed formats only — no `toLocale*`. A flight's date is its window's UTC
 * day: `new Date(iso).toISOString().slice(0, 10)`, the same UTC slicing the
 * intake cards' `formatWindow` uses (`apps/web/src/features/intake/flights/
 * format.ts`). That module lives in `apps/web`, which `apps/lyse` cannot
 * import; the five small formatters below are DUPLICATED from it on purpose
 * (same words, same real minus sign) rather than lifted into `packages/ui`,
 * which is barred for business vocabulary.
 *
 * Blob state (DI-D25 c4): `present` success, `withheld` NORMAL (neutral —
 * "withheld — stays on the pilot's PC"), `missing` a DEFECT (critical —
 * "n missing — a data defect"), `unknown` neutral "not checked (legacy)".
 * The counts are a detail read (`DataAcquisitionDetail.blobStateCounts`);
 * the list carries none and shows no blob chip (orchestrator decision 2).
 */
import type { StatusSlot } from "@aeriosiq/ui";
import type { LyseBlobStateCounts, LyseFlight } from "@aeriosiq/types";

const MINUS = "−";

/** The flight fields of a DA list row or a DA detail (all optional on the wire). */
export interface FlightBearing {
  flights?: readonly LyseFlight[] | undefined;
  flightCount?: number | undefined;
  enabledFlightCount?: number | undefined;
}

export interface ChipModel {
  key: string;
  label: string;
  slot: StatusSlot;
  tooltip?: string;
}

export interface FlightFacts {
  /** "2 flights" | "1 flight" | "—" (none, or the read failed). */
  flights: string;
  /** "2 on" (neutral) | "1 of 2 on" (attention); null without flights. */
  enabled: { label: string; slot: StatusSlot } | null;
  /** The FIRST enabled flight's offset + route; "n routes" when they differ. */
  offset: { label: string; tooltip: string } | null;
  /** Σ imageCount of the enabled flights; null without an enabled flight. */
  images: string | null;
  /** Any flight is the log of another operator's unit (DI-D56). */
  foreign: boolean;
}

// ── formatters (duplicated from apps/web intake/flights/format.ts) ─────────

/** `timeRoute` in words: "pose alignment", "shutter triggers", … */
export function formatRoute(
  route: LyseFlight["timeRoute"] | undefined,
): string {
  switch (route) {
    case "pose_alignment":
      return "pose alignment";
    case "trg":
      return "shutter triggers";
    case "leap_correction":
      return "GPS time, leap-corrected";
    default:
      return "no route";
  }
}

/** The list chip's short route word: "pose", "triggers", "GPS time", "no route". */
export function shortRoute(route: LyseFlight["timeRoute"] | undefined): string {
  switch (route) {
    case "pose_alignment":
      return "pose";
    case "trg":
      return "triggers";
    case "leap_correction":
      return "GPS time";
    default:
      return "no route";
  }
}

/** Signed, one decimal, a real minus sign: "−15.2 s", "+0.4 s", "0.0 s". */
export function formatOffset(s: number | null | undefined): string {
  if (s === null || s === undefined) return "—";
  const r = Math.round(s * 10) / 10;
  if (r === 0) return "0.0 s";
  return `${r < 0 ? MINUS : "+"}${Math.abs(r).toFixed(1)} s`;
}

/** One decimal: "0.7 m". */
export function formatResidual(m: number | null | undefined): string {
  if (m === null || m === undefined) return "—";
  return `${m.toFixed(1)} m`;
}

/** Degrees, one decimal, a real minus sign: "278.7°", "−3.7°". */
export function formatDegrees(d: number | null | undefined): string {
  if (d === null || d === undefined) return "—";
  const r = Math.round(d * 10) / 10;
  return `${r < 0 ? MINUS : ""}${Math.abs(r).toFixed(1)}°`;
}

/** "13:15:39–13:20:12 UTC" (the window as stored). */
export function formatWindow(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const t = (iso: string | null | undefined): string =>
    iso ? new Date(iso).toISOString().slice(11, 19) : "—";
  if (!start && !end) return "—";
  return `${t(start)}–${t(end)} UTC`;
}

/** "61 images", "1 image". */
export function formatImages(n: number): string {
  return `${String(n)} ${n === 1 ? "image" : "images"}`;
}

// ── dates ──────────────────────────────────────────────────────────────────

/** The window's UTC day, `YYYY-MM-DD`; null without a parseable instant. */
export function flightDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString().slice(0, 10);
}

/** The distinct UTC days the flights were flown on, ascending (DI-D39). */
export function flightDays(flights: readonly LyseFlight[]): string[] {
  const days = new Set<string>();
  for (const f of flights) {
    const d = flightDay(f.windowStartUtc);
    if (d) days.add(d);
  }
  return [...days].sort();
}

// ── facts ──────────────────────────────────────────────────────────────────

function countOf(da: FlightBearing): number {
  return da.flightCount ?? da.flights?.length ?? 0;
}

function enabledCountOf(da: FlightBearing): number {
  return (
    da.enabledFlightCount ?? da.flights?.filter((f) => f.enabled).length ?? 0
  );
}

export function flightFacts(da: FlightBearing): FlightFacts {
  const flights = da.flights ?? [];
  const count = countOf(da);
  if (count === 0) {
    return {
      flights: "—",
      enabled: null,
      offset: null,
      images: null,
      foreign: flights.some((f) => f.foreign),
    };
  }
  const on = enabledCountOf(da);
  const enabled =
    on < count
      ? {
          label: `${String(on)} of ${String(count)} on`,
          slot: "attention" as const,
        }
      : { label: `${String(on)} on`, slot: "neutral" as const };

  const enabledFlights = flights.filter((f) => f.enabled);
  const first = enabledFlights[0];
  let offset: FlightFacts["offset"] = null;
  if (first) {
    const routes = new Set(enabledFlights.map((f) => f.timeRoute ?? null));
    const tail =
      routes.size > 1
        ? `${String(routes.size)} routes`
        : shortRoute(first.timeRoute);
    offset = {
      label: `${formatOffset(first.timeOffsetS)} · ${tail}`,
      tooltip: enabledFlights
        .map(
          (f) =>
            `Flight ${String(f.flightNo)}: ${formatOffset(f.timeOffsetS)} · ${formatRoute(f.timeRoute)}`,
        )
        .join("\n"),
    };
  }
  const images =
    enabledFlights.length > 0
      ? formatImages(
          enabledFlights.reduce((s, f) => s + (f.imageCount ?? 0), 0),
        )
      : null;

  return {
    flights: `${String(count)} ${count === 1 ? "flight" : "flights"}`,
    enabled,
    offset,
    images,
    foreign: flights.some((f) => f.foreign),
  };
}

export const FOREIGN_LOG_WORDS = "another operator's log";

/** The list row's chips, in reading order; empty when the row has no flight. */
export function listChips(f: FlightFacts): ChipModel[] {
  if (f.flights === "—") {
    return f.foreign
      ? [{ key: "foreign", label: FOREIGN_LOG_WORDS, slot: "neutral" }]
      : [];
  }
  const chips: ChipModel[] = [
    { key: "flights", label: f.flights, slot: "neutral" },
  ];
  if (f.enabled) chips.push({ key: "enabled", ...f.enabled });
  if (f.offset)
    chips.push({
      key: "offset",
      label: f.offset.label,
      slot: "neutral",
      tooltip: f.offset.tooltip,
    });
  if (f.images) chips.push({ key: "images", label: f.images, slot: "neutral" });
  if (f.foreign)
    chips.push({ key: "foreign", label: FOREIGN_LOG_WORDS, slot: "neutral" });
  return chips;
}

// ── blob state ─────────────────────────────────────────────────────────────

export type BlobState = keyof LyseBlobStateCounts;

export interface BlobStateLine {
  state: BlobState;
  count: number;
  label: string;
  slot: StatusSlot;
}

const BLOB_ORDER: readonly BlobState[] = [
  "present",
  "withheld",
  "missing",
  "unknown",
];

const BLOB_SLOT: Record<BlobState, StatusSlot> = {
  present: "success",
  withheld: "neutral",
  missing: "critical",
  unknown: "neutral",
};

function blobWords(state: BlobState, n: number): string {
  const c = String(n);
  switch (state) {
    case "present":
      return `${c} present`;
    case "withheld":
      return `${c} withheld — stays on the pilot's PC`;
    case "missing":
      return `${c} missing — a data defect`;
    case "unknown":
      return `${c} not checked (legacy)`;
  }
}

/** The detail's blob-state line, zero counts left out (DI-D25 c4). */
export function blobStateLines(
  counts: LyseBlobStateCounts | undefined,
): BlobStateLine[] {
  if (!counts) return [];
  return BLOB_ORDER.filter((s) => counts[s] > 0).map((state) => ({
    state,
    count: counts[state],
    label: blobWords(state, counts[state]),
    slot: BLOB_SLOT[state],
  }));
}
